import { BsFilePerson } from 'react-icons/bs';
import { Candidato } from '../Candidato/Candidato';
import Pessoa from '../Pessoa';
import MembroBanca, { PapelNome, StatusMembroNome } from './MembroBanca';
import { Votacao } from './votacao/Votacao';
import { FaDesktop, FaGavel } from 'react-icons/fa';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

export enum TipoBancaNome {
    FACIAL = 'Facial',
    VIRTUAL = 'Virtual',
    RECURSAL = 'Recursal',
}

export enum StatusBanca {
    INDEFINIDA = 'Banca Criada',
    PLANEJADA = 'Banca Planejada',
    CONSULTADA = 'Banca Disponibilidade Consultada',
    DISPONIBILIDADE = 'Banca Disponibilidade Confirmada',
    DEFINIDA = 'Banca Definida',
    CONVOCADA = 'Banca Convocada',
    INTEGRACAO = 'Banca em Integração',
    FINALIZADA = 'Banca Finalizada',
    CANCELADA = 'Banca Cancelada',
    FECHADA = 'Banca Fechada',
}

export interface TipoBanca {
    id: string | null;
    nome: TipoBancaNome;
    categoriaEdital?: string | null;
}

export interface DataDeBanca {
    data: string | null;
    horaInicial: string | null;
    horaFinal: string | null;
    confirmada: boolean | null;
}

export default interface Banca {
    id: string;
    tipoBanca: TipoBanca;
    membros: MembroBanca[];
    status: StatusBanca;
    dataDeBanca: DataDeBanca | null;
    salaDeBanca: {
        id: string | null;
        link: string | null;
    };
    candidatos?: Candidato[];
    votacoes?: Votacao[];
    processoId?: string;
    finalizavel?: boolean;
    formaRealizacao?: string | null;
    hasIntegracao?: boolean;
}

export class BancaUtils {

    static isDataAnterior(banca: Banca): boolean {
        if (!banca.dataDeBanca?.data) {
            return false; // Se não houver data, considera como não anterior
        }

        const dataBanca = new Date(banca.dataDeBanca.data);
        const dataAtual = new Date();

        // Compara apenas ano, mês e dia, ignorando horas
        return dataBanca < new Date(dataAtual.getFullYear(), dataAtual.getMonth(), dataAtual.getDate() - 1);
    }


    static isBancaFechavel(banca: Banca): boolean {
        if (banca.dataDeBanca == null) {
            return false;
        }
        if (this.isDataAnterior(banca) && banca.status !== StatusBanca.FINALIZADA && banca.status !== StatusBanca.FECHADA && banca.status !== StatusBanca.INDEFINIDA && banca.status) {
            //console.log(banca.tipoBanca.nome + ": " + "fechável")
            return true;
        }
        return false;
    }

    static mapStringToTipoBancaNome(nome: string): TipoBancaNome {
        switch (nome) {
            case 'Facial':
                return TipoBancaNome.FACIAL;
            case 'Virtual':
                return TipoBancaNome.VIRTUAL;
            case 'Recursal':
                return TipoBancaNome.RECURSAL;
            default:
                throw new Error(`Unknown TipoBancaNome: ${nome}`);
        }
    }

    static getIconeBanca(tipoBanca: TipoBancaNome): any {
        const renderTooltip = (text: string) => <Tooltip id="button-tooltip">{text}</Tooltip>;

        // Estilo para o container do ícone
        const iconStyle = {
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '30px',  // Largura fixa
            height: '30px', // Altura fixa
        };

        switch (tipoBanca) {
            case TipoBancaNome.FACIAL:
                return (
                    <OverlayTrigger
                        placement="top"
                        delay={{ show: 250, hide: 400 }}
                        overlay={renderTooltip(tipoBanca.split('Banca de Heteroidentificação').join(''))}
                        offset={[0, 10]} // Ajuste fino do posicionamento (horizontal, vertical)
                    >
                        <div style={iconStyle}>
                            <BsFilePerson />
                        </div>
                    </OverlayTrigger>
                );
            case TipoBancaNome.RECURSAL:
                return (
                    <OverlayTrigger
                        placement="top"
                        delay={{ show: 250, hide: 400 }}
                        overlay={renderTooltip(tipoBanca.split('Banca de Heteroidentificação').join(''))}
                        offset={[0, 10]}
                    >
                        <div style={iconStyle}>
                            <FaGavel />
                        </div>
                    </OverlayTrigger>
                );
            case TipoBancaNome.VIRTUAL:
                return (
                    <OverlayTrigger
                        placement="top"
                        delay={{ show: 250, hide: 400 }}
                        overlay={renderTooltip(tipoBanca.split('Banca de Heteroidentificação').join(''))}
                        offset={[0, 10]}
                    >
                        <div style={iconStyle}>
                            <FaDesktop />
                        </div>
                    </OverlayTrigger>
                );
            default:
                return '';
        }
    }

    static getBancaById(bancas: Banca[], id: string): Banca | undefined {
        return bancas.find((banca) => banca.id === id);
    }
    static getStatusArray(): StatusBanca[] {
        return [
            StatusBanca.INDEFINIDA,
            StatusBanca.PLANEJADA,
            StatusBanca.CONSULTADA,
            StatusBanca.DISPONIBILIDADE,
            StatusBanca.DEFINIDA,
            StatusBanca.CONVOCADA,
            StatusBanca.INTEGRACAO,
            StatusBanca.FINALIZADA,
            StatusBanca.CANCELADA,
            StatusBanca.FECHADA,
        ];
    }

    static mapStringToStatusBanca(status: string): StatusBanca {
        switch (status) {
            case 'INDEFINIDA':
                return StatusBanca.INDEFINIDA;
            case 'PLANEJADA':
                return StatusBanca.PLANEJADA;
            case 'CONSULTADA':
                return StatusBanca.CONSULTADA;
            case 'DISPONIBILIDADE':
                return StatusBanca.DISPONIBILIDADE;
            case 'DEFINIDA':
                return StatusBanca.DEFINIDA;
            case 'CONVOCADA':
                return StatusBanca.CONVOCADA;
            case 'INTEGRACAO':
                return StatusBanca.INTEGRACAO;
            case 'FINALIZADA':
                return StatusBanca.FINALIZADA;
            case 'CANCELADA':
                return StatusBanca.CANCELADA;
            case 'FECHADA':
                return StatusBanca.FECHADA;
            default:
                throw new Error('Status desconhecido');
        }
    }

    static getStatusBancaString(status: StatusBanca): string {
        switch (status) {
            case StatusBanca.INDEFINIDA:
                return 'Criada';
            case StatusBanca.PLANEJADA: //Não implementada no Backend
                return 'Banca Planejada';
            case StatusBanca.CONSULTADA: //Não implementada no Backend
                return 'Disponibilidade Consultada';
            case StatusBanca.DISPONIBILIDADE: //Não implementada no Backend
                return 'Disponibilidade Confirmada';
            case StatusBanca.DEFINIDA:
                return 'Banca Definida';
            case StatusBanca.CONVOCADA:
                return 'Banca Convocada';
            case StatusBanca.INTEGRACAO:
                return 'Banca Integração';
            case StatusBanca.FINALIZADA:
                return 'Banca Finalizada';
            case StatusBanca.CANCELADA:
                return 'Banca Cancelada';
            case StatusBanca.FECHADA:
                return 'Banca Fechada';
            default:
                return 'Criada';
        }
    }

    static getRealStatusBanca(banca: Banca): StatusBanca {
        const titulares = banca.membros.filter(m => m.papelMembro.nome === PapelNome.TITULAR)
        switch (banca.status) {
            case StatusBanca.INDEFINIDA:
                if (titulares.length === 5) {
                    if (titulares.every(m => m.statusMembro === StatusMembroNome.DEFINIDO)) {
                        return StatusBanca.DEFINIDA
                    }
                    if (titulares.every(m => m.statusMembro === StatusMembroNome.CONFIRMADO)) {
                        return StatusBanca.DISPONIBILIDADE
                    }
                    if (titulares.find(m => m.statusMembro === StatusMembroNome.CONSULTADO)) {
                        return StatusBanca.CONSULTADA
                    }
                }
                if (banca.dataDeBanca !== null) {
                    return StatusBanca.PLANEJADA
                }
                return StatusBanca.INDEFINIDA
            case StatusBanca.PLANEJADA: //Não implementada no Backend
                return StatusBanca.PLANEJADA;
            case StatusBanca.CONSULTADA: //Não implementada no Backend
                return StatusBanca.CONSULTADA;
            case StatusBanca.DISPONIBILIDADE: //Não implementada no Backend
                return StatusBanca.DISPONIBILIDADE;
            case StatusBanca.DEFINIDA:
                return StatusBanca.DEFINIDA;
            case StatusBanca.CONVOCADA:
                return StatusBanca.CONVOCADA;
            case StatusBanca.INTEGRACAO:
                return StatusBanca.INTEGRACAO;
            case StatusBanca.FINALIZADA:
                return StatusBanca.FINALIZADA;
            case StatusBanca.CANCELADA:
                return StatusBanca.CANCELADA;
            case StatusBanca.FECHADA:
                return StatusBanca.FECHADA;
            default:
                return StatusBanca.INDEFINIDA;
        }
    }

    static isStatusPosteriorOuIgual(statusAnterior: StatusBanca, statusAtual: StatusBanca): boolean {
        const statusArray = this.getStatusArray();
        return statusArray.indexOf(statusAtual) >= statusArray.indexOf(statusAnterior);
    }

    static isStatusPosterior(statusAnterior: StatusBanca, statusAtual: StatusBanca): boolean {
        const statusArray = this.getStatusArray();
        return statusArray.indexOf(statusAtual) > statusArray.indexOf(statusAnterior);
    }

    static isStatusAnteriorOuIgual(statusAnterior: StatusBanca, statusAtual: StatusBanca): boolean {
        const statusArray = this.getStatusArray();
        return statusArray.indexOf(statusAtual) <= statusArray.indexOf(statusAnterior);
    }

    static isStatusAnterior(statusAnterior: StatusBanca, statusAtual: StatusBanca): boolean {
        const statusArray = this.getStatusArray();
        return statusArray.indexOf(statusAtual) < statusArray.indexOf(statusAnterior);
    }

    static existeBancaComStatusPosteriorOuIgual(bancas: Banca[], statusAnterior: StatusBanca): boolean {
        if (bancas.length > 0) {
            const statusArray = this.getStatusArray();
            return bancas.some(banca =>
                statusArray.indexOf(BancaUtils.getRealStatusBanca(banca)) >= statusArray.indexOf(statusAnterior)
            );
        }
        return false;
    }

    static existeBancaComStatusInferiorOuIgual(bancas: Banca[], statusAnterior: StatusBanca): boolean {
        if (bancas.length > 0) {
            const statusArray = this.getStatusArray();
            return bancas.some(banca =>
                statusArray.indexOf(BancaUtils.getRealStatusBanca(banca)) <= statusArray.indexOf(statusAnterior)
            );
        }
        return false;
    }

    static existeBancaFechavel(bancas: Banca[]): boolean {
        if (bancas.length > 0) {
            return bancas.some(banca =>
                this.isBancaFechavel(banca)
            );
        }
        return false;
    }

    static existeBancaFinalizavel(bancas: Banca[]): boolean {
        if (bancas.length > 0) {
            return bancas.some(banca =>
                banca.finalizavel && banca.status === StatusBanca.INTEGRACAO
            );
        }
        return false;
    }

    static getMembrosPorPapel(banca: Banca, papelNome: PapelNome): MembroBanca[] {
        return banca.membros.filter((membro) => membro.papelMembro.nome === papelNome);
    }

    static getPessoasPorPapelEmPessoa(banca: Banca, papelNome: PapelNome): Pessoa[] {
        const membrosPorPapel = BancaUtils.getMembrosPorPapel(banca, papelNome);
        return membrosPorPapel.map((membro) => membro.pessoa);
    }

    static substituirMembrosPorPapel(banca: Banca, papelNome: PapelNome, novosMembros: MembroBanca[]): Banca {
        const membrosAtualizados = banca.membros.filter((membro) => membro.papelMembro.nome !== papelNome);

        const bancaAtualizada: Banca = {
            ...banca,
            membros: [...membrosAtualizados, ...novosMembros],
        };

        return bancaAtualizada;
    }

    static adicionarMembro(
        banca: Banca,
        novoMembro: Pessoa,
        papelMembroNome: PapelNome,
        statusMembro: StatusMembroNome
    ): void {
        const novoMembroBanca: MembroBanca = {
            id: null,
            pessoa: novoMembro,
            papelMembro: {
                id: null,
                nome: papelMembroNome,
            },
            statusMembro: statusMembro,
        };
        banca.membros.push(novoMembroBanca);
    }

    static formatarDataDeBanca(
        dataDeBanca: { data: string | null; horaInicial: string | null; horaFinal: string | null } | null
    ): string {
        if (dataDeBanca && dataDeBanca.data && dataDeBanca.horaInicial && dataDeBanca.horaFinal) {
            const [ano, mes, dia] = dataDeBanca.data.split('-');
            const horaInicial = dataDeBanca.horaInicial.slice(0, 5);
            const horaFinal = dataDeBanca.horaFinal.slice(0, 5);

            return `${dia}/${mes}/${ano} - ${horaInicial}-${horaFinal}`;
        } else {
            return '-';
        }
    }

    static parseDataDeBanca(
        dataString: string,
        confirmada: boolean
    ): { data: string | null; horaInicial: string | null; horaFinal: string | null; confirmada: boolean | null } {
        if (dataString && confirmada) {
            const [dataPart, horasPart] = dataString.split(' - ');
            const [dia, mes, ano] = dataPart.split('/');
            const [horaInicial, horaFinal] = horasPart.split('-');

            return {
                data: `${ano}-${mes}-${dia}`,
                horaInicial: `${horaInicial}:00`,
                horaFinal: `${horaFinal}:00`,
                confirmada: confirmada,
            };
        } else {
            return {
                data: null,
                horaInicial: null,
                horaFinal: null,
                confirmada: null,
            };
        }
    }

    static limparMembros(banca: Banca): Banca {
        return {
            ...banca,
            membros: [],
        };
    }

    static isBotaoDisabled(statusBanca: StatusBanca, botao: string): boolean {
        const StatusBancaValues: { [key in StatusBanca]: number } = {
            [StatusBanca.INDEFINIDA]: 1,
            [StatusBanca.PLANEJADA]: 2,
            [StatusBanca.CONSULTADA]: 3,
            [StatusBanca.DISPONIBILIDADE]: 4,
            [StatusBanca.DEFINIDA]: 5,
            [StatusBanca.CONVOCADA]: 6,
            [StatusBanca.INTEGRACAO]: 7,
            [StatusBanca.FINALIZADA]: 8,
            [StatusBanca.CANCELADA]: 9,
            [StatusBanca.FECHADA]: 10,
        };

        const statusValue = StatusBancaValues[statusBanca];

        switch (botao) {
            case 'sala':
                return false;
            case 'data':
                return !(statusValue === 1);
            case 'composicao':
                return !(statusValue === 2 || statusValue === 3 || statusValue === 4);
            case 'consulta':
                return !(statusValue === 2);
            case 'definicao':
                return !(statusValue === 4);
            case 'convoca':
                return !(statusValue === 5);
            case 'emergencia':
                return !(statusValue === 6);
        }
        return false;
    }

    static getStatusColor = (status: StatusBanca): string => {
        switch (status) {
            case StatusBanca.INDEFINIDA:
                return '#ffcccc';
            case StatusBanca.PLANEJADA:
                return '#ffeb99';
            case StatusBanca.CONSULTADA:
                return '#c2f0c2';
            case StatusBanca.DISPONIBILIDADE:
                return '#99ccff';
            case StatusBanca.DEFINIDA:
                return '#cc99ff';
            case StatusBanca.CONVOCADA:
                return '#ffb366';
            case StatusBanca.INTEGRACAO:
                return '#ffffff';
            case StatusBanca.FINALIZADA:
                return '#66cc99';
            case StatusBanca.CANCELADA:
                return '#cccccc';
            case StatusBanca.FECHADA:
                return '#6699cc';
            default:
                return '#ffffff';
        }
    };

    static getStatusExplanation = (status: StatusBanca): string => {
        switch (status) {
            case StatusBanca.INDEFINIDA:
                return 'A banca foi criada mas ainda não foi planejada ou definida';
            case StatusBanca.PLANEJADA:
                return 'A banca tem data definida';
            case StatusBanca.CONSULTADA:
                return 'A disponibilidade dos membros da banca foi consultada, mas ainda não confirmada';
            case StatusBanca.DISPONIBILIDADE:
                return 'Há disponibilidade de membros confirmada, pode-se defini-los como membros (definitivos)';
            case StatusBanca.DEFINIDA:
                return 'A banca foi definida e aguarda convocação';
            case StatusBanca.CONVOCADA:
                return 'Os membros da banca foram formalmente convocados para a Heteroidentificação';
            case StatusBanca.INTEGRACAO:
                return 'Candidatos enviados por Unidade Externa (Banca Facial), devem ser checados pela DEDH';
            case StatusBanca.FINALIZADA:
                return 'A banca foi realizada e concluída com sucesso';
            case StatusBanca.CANCELADA:
                return 'A banca foi cancelada por membro da DEDH, motivado por algum erro em seu planejamento inicial';
            case StatusBanca.FECHADA:
                return 'A banca foi encerrada por desuso (Não houveram Candidatos)';
            default:
                return 'Status não definido.';
        }
    };
}
